/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import Link from "next/link";
import { Component } from "react";
import LazyLoad from "react-lazy-load";

import Banner from "../../components/banner";
import Newsletter from "../../components/newsletter";
import OpenGraphHead from "../../components/og-head";
import TwitterHead from "../../components/twitter-head";
import {
  toAbsolutePageURL,
  toRelativePageURL,
} from "../../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class GirlsInc extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/partners/girls-inc", market);
    const alt = "Lasting Smiles";
    const title = "Girls Inc. · Partners · Lasting Smiles";
    const description = "Give back to Girls Inc. with Lasting Smiles organic and all natural lip balm. " +
      "Smile for a cause. " +
      "Give back to charitable causes you care about with sustainable, ethical, and organic skin care products that make a difference all year long. " +
      "The #1 socially responsible and ethical lip care and do good lip balm choice to support your favorite social and environmental non-profit causes caring for people and our planet. " +
      "The perfect gift that gives back.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/partners/girls-inc", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/partners/girls-inc", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/purpose", market),
                    "name": "Causes",
                    "position": 2,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/partners/girls-inc", market),
                    "name": "Girls Inc.",
                    "position": 3,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <Banner />
        <Newsletter />
        <section
          className="bg-yellow-100 px-2 py-10 sm:px-5"
          style={{
            backgroundImage: `url("${require("../../public/images/backgrounds/purpose.jpg")}")` as string,
            backgroundPositionY: "center",
            backgroundSize: "cover",
          }}
        >
          <div className="max-w-screen-lg mx-auto">
            <h2
              className="font-extrabold font-serif sm:text-6xl text-5xl text-center uppercase"
              style={{
                background: "linear-gradient(to bottom right, #FAC551, #F36F48, #F3B411, #F7A4B6)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Smile for a&nbsp;Cause
            </h2>
            <picture>
              <source
                srcSet={require("../../public/images/logos/girls-inc-wide.webp") as string}
                type="image/webp"
              />
              <source
                srcSet={require("../../public/images/logos/girls-inc-wide.png") as string}
                type="image/png"
              />
              <img
                alt="Girls Inc."
                className="block mx-auto"
                src={require("../../public/images/logos/girls-inc-wide.png") as string}
                style={{ maxHeight: 200 }}
              />
            </picture>
          </div>
        </section>
        <section className="py-10">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif px-2 sm:px-5 text-3xl text-center">
              Girls&nbsp;Inc.
            </h1>
            <p className="px-2 sm:px-5 text-xl text-center">
              Our mission is to inspire all girls to be strong, smart, and bold, through direct service and advocacy.
              Join us in supporting girls on their journey to overcome obstacles and thrive.
            </p>
            <div className="overflow-x-auto overflow-y-hidden scrolling-touch sm:max-w-screen-md sm:mx-auto sm:px-5">
              <div className="flex-row inline-flex sm:flex sm:flex-wrap sm:items-stretch sm:justify-around text-white">
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Girls Inc. Photo 1"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/girls-inc/photo-1.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Girls Inc. Photo 2"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/girls-inc/photo-2.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-3.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-3.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Girls Inc. Photo 3"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/girls-inc/photo-3.jpg") as string}
                    />
                  </picture>
                </div>
                <div className="box-border flex-initial font-medium font-serif mx-2 my-5 sm:min-w-auto sm:mx-5 rounded text-center">
                  <picture>
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-4.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../../public/images/partners/girls-inc/photo-4.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Girls Inc. Photo 4"
                      className="max-w-250 rounded sm:max-w-300"
                      src={require("../../public/images/partners/girls-inc/photo-4.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
            </div>
            <p className="px-2 sm:px-5 text-xl text-center">
              We equip girls to navigate gender, economic, and social barriers and grow up healthy, educated, and independent.
              These positive outcomes are achieved through mentoring relationships, physically and emotionally safe environments, and engaging programming &ndash; meeting the needs of today's girls.
            </p>
          </div>
        </section>
        <section className="bg-gray-100 px-2 py-10 sm:px-5">
          <div className="max-w-screen-md mx-auto">
            <h2 className="font-medium font-serif text-2xl text-center">
              Meet Ariel, a Girls Inc. alumna who shares how her experience at Girls Inc. helped her find her self-confidence and career path!
            </h2>
            <p className="my-5">
              <picture>
                <source
                  srcSet={require("../../public/images/partners/girls-inc/photo-ariel.webp") as string}
                  type="image/webp"
                />
                <source
                  srcSet={require("../../public/images/partners/girls-inc/photo-ariel.jpg") as string}
                  type="image/jpeg"
                />
                <img
                  alt="Photo of Ariel"
                  className="mx-auto rounded"
                  src={require("../../public/images/partners/girls-inc/photo-ariel.jpg") as string}
                />
              </picture>
            </p>
            <p className="text-center">
              When I joined Girls Inc. as a shy 10-year-old, I could not have imagined going to college, much less attending Georgetown University for graduate school or serving on Girls Inc. of Dothan, Alabama's board.
              Girls Inc. profoundly shaped my path in life.
              Today, I am the Office Administrator of an ophthalmology research group and the Physician Liaison and Marketing Coordinator at a surgery center.
              I draw on the mentoring relationships and the confidence I built at Girls Inc.
              I am so grateful for the life-changing opportunities I have had through Girls Inc.
            </p>
            <div className="text-center">
              <Link
                as={toRelativePageURL("/products", market as string)}
                href={{
                  pathname: "/products",
                  query: { market },
                }}
              >
                <a className="bg-black border border-black duration-500 ease-in-out font-medium font-serif hover:bg-default hover:text-black inline-block my-5 px-5 py-3 rounded shadow uppercase text-lg text-white transition">
                  Shop &amp; Make a Difference
                </a>
              </Link>
            </div>
          </div>
        </section>
      </>
    );
  }
}
